/**
 * Market Flow — Vue Heatmap / Trend (3ème vue)
 * Affiche les tendances par catégorie en mode Heatmap ou Lignes (responsive, traduit).
 */
import React, { useMemo, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

/* ---------------- Helpers (alignés avec MarketFlow) ---------------- */
const fmtPct = (v) => `${v >= 0 ? "+" : ""}${Number(v).toFixed(2)}%`;

const tone = {
  green: "#6f8a5b",
  green2: "#8da57a",
  olive: "#a9b38a",
  amber: "#caa85e",
  orange: "#d28a52",
  red: "#c6523f",
};

function hexToRgb(hex) {
  const h = String(hex).replace("#", "").trim();
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}
function rgbToHex(r, g, b) {
  const clamp = (x) => Math.max(0, Math.min(255, Math.round(x)));
  const to = (x) => clamp(x).toString(16).padStart(2, "0");
  return `#${to(r)}${to(g)}${to(b)}`;
}
function lerp(a, b, t) {
  return a + (b - a) * t;
}
function clamp01(x) {
  return Math.max(0, Math.min(1, x));
}
function lerpColor(aHex, bHex, t) {
  const a = hexToRgb(aHex);
  const b = hexToRgb(bHex);
  return rgbToHex(lerp(a.r, b.r, t), lerp(a.g, b.g, t), lerp(a.b, b.b, t));
}
/** Échelle de référence en % : chaque pourcentage donne une nuance distincte (pas écrasée par les extrêmes). */
const HEAT_REF_PCT = 60;
/** Plusieurs paliers vert/rouge pour des nuances bien visibles entre chaque %. */
const GREEN_STOPS = [tone.green2, "#8a9f6e", "#7a9359", tone.green];
const RED_STOPS = [tone.orange, "#d16a4a", "#cc5a42", tone.red];

function colorFromStops(stops, t) {
  const T = clamp01(t);
  const seg = (stops.length - 1) * T;
  const i = Math.min(stops.length - 2, Math.max(0, Math.floor(seg)));
  const localT = seg - i;
  return lerpColor(stops[i], stops[i + 1], localT);
}

/** Négatif → rouge (plus foncé si plus négatif). Positif → vert (plus foncé si plus positif). Zéro → neutre. */
function colorForValue(v, min, max) {
  if (!Number.isFinite(v)) return tone.olive;
  if (v < 0) {
    const raw = clamp01((-v) / HEAT_REF_PCT);
    const intensity = Math.pow(raw, 0.65);
    return colorFromStops(RED_STOPS, intensity);
  }
  if (v > 0) {
    const raw = clamp01(v / HEAT_REF_PCT);
    const intensity = Math.pow(raw, 0.65);
    return colorFromStops(GREEN_STOPS, intensity);
  }
  return tone.olive;
}

function valueColor(v) {
  if (!Number.isFinite(v) || v === 0) return "rgba(31,42,36,.72)";
  return v > 0 ? tone.green : tone.red;
}

/** Couleur pour les % du tooltip : 0 = gris, positif = vert (de plus en plus vert), négatif = rouge (de plus en plus rouge) */
function pctColor(v) {
  if (!Number.isFinite(v)) return "#6f7b74";
  if (Math.abs(v) < 0.01) return "#6f7b74"; // gris pour ~0
  if (v > 0) {
    const t = clamp01(Math.min(1, v / 40)); // 40%+ = vert foncé
    return lerpColor(tone.green2, tone.green, t);
  }
  const t = clamp01(Math.min(1, -v / 40)); // -40% = rouge foncé
  return lerpColor(tone.orange, tone.red, t);
}

function lastValue(row) {
  return row.points && row.points.length ? row.points[row.points.length - 1]?.v ?? 0 : 0;
}
function delta1m(row) {
  const n = (row.points && row.points.length) || 0;
  if (n < 2) return 0;
  return (row.points[n - 1]?.v ?? 0) - (row.points[n - 2]?.v ?? 0);
}
function slopeSimple(row) {
  const y = (row.points || []).map((p) => p.v);
  const n = y.length;
  if (n < 2) return 0;
  const xMean = (n - 1) / 2;
  const yMean = y.reduce((a, b) => a + b, 0) / n;
  let num = 0;
  let den = 0;
  for (let i = 0; i < n; i++) {
    num += (i - xMean) * (y[i] - yMean);
    den += (i - xMean) ** 2;
  }
  return den === 0 ? 0 : num / den;
}

function mergeSeriesForChart(rows) {
  const months = rows[0]?.points?.map((p) => p.m) ?? [];
  return months.map((m, idx) => {
    const obj = { m };
    rows.forEach((r) => {
      obj[r.id] = r.points && r.points[idx] ? r.points[idx].v : null;
    });
    return obj;
  });
}

function hashToInt(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
function colorForSeries(id) {
  const seed = hashToInt(String(id));
  const GOLDEN_ANGLE = 137.50776405003785;
  const hue = (seed * GOLDEN_ANGLE) % 360;
  return `hsl(${hue.toFixed(1)}, 68%, 42%)`;
}

function abbrFor(title, name) {
  const n = String(name).trim();
  const t = String(title || "").toLowerCase();
  if (t.includes("region")) {
    const map = { "North America": "NA", Europe: "EU", "South Asia": "SAs", "Southeast Asia": "SEA", Africa: "AF", LatAm: "LATAM", "Latin America": "LATAM", "Middle East": "ME", MENA: "MENA", Oceania: "OCN", Nordics: "NORD", "Eastern Europe": "EE", CEE: "CEE" };
    return map[n] ?? n;
  }
  if (t.includes("sector") || t.includes("secteur")) {
    const map = { Communication: "Comm", "Communication Services": "Comm", "Consumer Discretionary": "Disc", "Consumer Staples": "Stap", "Information Technology": "Tech", Technology: "Tech", Financials: "Fin", Industrials: "Ind", "Health Care": "Hlth", Healthcare: "Hlth", Materials: "Mats", "Real Estate": "RE", Utilities: "Util", Energy: "Engy" };
    return map[n] ?? n;
  }
  if (t.includes("market cap") || t.includes("capitalisation")) {
    const map = { "Mega Cap": "Mega", "Large Cap": "Lrg", "Mid Cap": "Mid", "Small Cap": "Sm", "Nano Cap": "Nano" };
    return map[n] ?? n;
  }
  if (t.includes("fundamental")) {
    const map = { Excellent: "Excl", Good: "Good", Average: "Avg", Bad: "Bad", Worst: "Wrst" };
    return map[n] ?? n;
  }
  if (t.includes("style")) {
    const map = { Quality: "Qual", Growth: "Grow", Value: "Val", Income: "Inc", Core: "Core", "High Quality": "HiQual" };
    return map[n] ?? n;
  }
  return n;
}

function displayName(title, fullName, translateData) {
  const a = abbrFor(title, fullName);
  if (translateData && typeof translateData === "function") {
    const translated = translateData(fullName);
    if (translated !== fullName) return translated.length <= 8 ? translated : translated.slice(0, 7) + "…";
  }
  return a.length <= 8 ? a : a.slice(0, 7) + "…";
}

/* ---------------- Heatmap tooltip ---------------- */
function HeatTip({ tip, t }) {
  if (!tip) return null;
  return (
    <div
      style={{
        position: "fixed",
        left: Math.max(12, tip.x - 140),
        top: Math.max(12, tip.y - 74),
        width: 280,
        zIndex: 50,
        pointerEvents: "none",
        background: "rgba(255,255,255,.96)",
        border: "1px solid rgba(30,40,34,.12)",
        borderRadius: 14,
        boxShadow: "0 10px 20px rgba(16,24,20,.10)",
        padding: "10px 12px",
      }}
    >
      <div style={{ fontWeight: 950, marginBottom: 6, color: "rgba(31,42,36,.86)" }}>{tip.month}</div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
        <div style={{ fontWeight: 900, color: "rgba(31,42,36,.78)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tip.rowName}</div>
        <div style={{ fontWeight: 950, color: valueColor(tip.value), fontVariantNumeric: "tabular-nums" }}>{fmtPct(tip.value)}</div>
      </div>
      <div style={{ marginTop: 6, fontSize: 11, color: "rgba(31,42,36,.60)", fontWeight: 800 }}>{t("heatmapColumnSortedDesc")}</div>
    </div>
  );
}

/* ---------------- Trend card (Heatmap or Lines) ---------------- */
function TrendCard({ title, rows, months, sortKey, mode, t, translateData }) {
  const [topN, setTopN] = useState(10);
  const [hidden, setHidden] = useState({});
  const [tip, setTip] = useState(null);
  const [useCentralZoom, setUseCentralZoom] = useState(true);

  const score = (r) => (sortKey === "last" ? lastValue(r) : sortKey === "delta1m" ? delta1m(r) : slopeSimple(r));

  const rankedBySortKey = useMemo(() => {
    const copy = [...(rows || [])];
    copy.sort((a, b) => score(b) - score(a));
    return copy;
  }, [rows, sortKey]);

  const effectiveTop = useMemo(() => {
    if (topN === "all") return rankedBySortKey;
    return rankedBySortKey.slice(0, topN);
  }, [rankedBySortKey, topN]);

  const columnRankings = useMemo(() => {
    return (months || []).map((_, colIdx) => {
      const col = [...effectiveTop].filter((r) => !hidden[r.id]);
      col.sort((a, b) => ((b.points && b.points[colIdx]?.v) ?? 0) - ((a.points && a.points[colIdx]?.v) ?? 0));
      return col;
    });
  }, [months, effectiveTop, hidden]);

  const visibleSeries = useMemo(() => effectiveTop.filter((r) => !hidden[r.id]), [effectiveTop, hidden]);

  const { minV, maxV } = useMemo(() => {
    const vals = [];
    visibleSeries.forEach((r) => (r.points || []).forEach((p) => vals.push(p.v)));
    if (vals.length === 0) effectiveTop.slice(0, 1).forEach((r) => (r.points || []).forEach((p) => vals.push(p.v)));
    if (vals.length === 0) return { minV: 0, maxV: 0 };
    return { minV: Math.min(...vals), maxV: Math.max(...vals) };
  }, [visibleSeries, effectiveTop]);

  const linesData = useMemo(() => mergeSeriesForChart(visibleSeries), [visibleSeries]);
  // Ordre chronologique pour le graphique : plus ancien à gauche, plus récent à droite
  const linesDataForChart = useMemo(() => linesData || [], [linesData]);

  // Domaine Y "zoom zone centrale" : -100 % à +100 %, valeurs hors plage coupées (allowDataOverflow)
  const focusYDomain = [-100, 1000];

  const isCountries = title === t("countries") || title === "Countries" || title === "Pays";

  if (!rows || rows.length === 0) {
    return (
      <div className="td-trendBox">
        <div className="td-trendHead">
          <div className="td-trendTitle">{title}</div>
        </div>
        <div className="td-trendInner">
          <p style={{ fontSize: 13, color: "var(--muted)", fontWeight: 800 }}>{t("noDataAvailable")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="td-trendBox">
      <div className="td-trendHead">
        <div>
          <div className="td-trendTitle">{title}</div>
          <div className="td-trendMeta">
            {mode === "heatmap" ? t("heatmapMode") : t("linesMode")} · {mode === "heatmap" ? t("heatmapEachColumnSorted") : t("sortedBySortKey")}
          </div>
        </div>
        <div className="td-trendControls">
          {mode === "lines" ? (
            <button
              type="button"
              className="td-ghostBtn"
              onClick={() => setUseCentralZoom((z) => !z)}
              title={useCentralZoom ? t("scaleAuto") : t("zoomCentralZone")}
              style={useCentralZoom ? { background: "rgba(181,154,91,.18)", borderColor: "rgba(181,154,91,.4)" } : {}}
            >
              {useCentralZoom ? t("scaleAuto") : t("zoomCentralZone")}
            </button>
          ) : null}
          {isCountries ? (
            <select
              className="td-miniSelect"
              value={String(topN)}
              onChange={(e) => setTopN(e.target.value === "all" ? "all" : Number(e.target.value))}
              aria-label={`${title} ${t("topN")}`}
            >
              {[3, 5, 10, 15, 20].map((n) => (
                <option key={n} value={String(n)}>Top {n}</option>
              ))}
              <option value="all">{t("all")}</option>
            </select>
          ) : null}
          <button type="button" className="td-ghostBtn" onClick={() => setHidden({})}>
            {t("reset")}
          </button>
        </div>
      </div>

      <div className="td-trendInner">
        {mode === "lines" ? (
          <>
            <div className="td-mapWrap" style={{ background: "#fff", padding: 10, borderRadius: 14, border: "1px solid rgba(30,40,34,.10)" }}>
              <div style={{ width: "100%", height: 620, minHeight: 500 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={linesDataForChart} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} strokeOpacity={0.2} />
                    <XAxis dataKey="m" tick={{ fontSize: 11 }} />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      width={40}
                      domain={useCentralZoom ? focusYDomain : ["auto", "auto"]}
                      allowDataOverflow={useCentralZoom}
                      tickFormatter={(v) => String(Math.round(Number(v) / 10) * 10)}
                    />
                    <Tooltip
                      formatter={(v) => [v != null ? fmtPct(Number(v)) : "—", ""]}
                      contentStyle={{ borderRadius: 12, border: "1px solid rgba(0,0,0,.10)", boxShadow: "0 10px 20px rgba(0,0,0,.10)" }}
                      content={({ active, payload, label }) => {
                        if (!active || !payload || !payload.length) return null;
                        const cleaned = payload
                          .filter((p) => p && typeof p.value === "number")
                          .map((p) => {
                            const series = visibleSeries.find((r) => r.id === p.name);
                            const displayLabel = series ? (translateData ? translateData(series.name) : series.name) : displayName(title, p.name, translateData);
                            return { id: p.name, name: displayLabel, value: Number(p.value), color: p.stroke || p.color };
                          });
                        // Du plus négatif en bas au plus positif en haut
                        cleaned.sort((a, b) => b.value - a.value);
                        return (
                          <div style={{ padding: 10, background: "rgba(255,255,255,.96)", borderRadius: 12, border: "1px solid rgba(0,0,0,.10)" }}>
                            <div style={{ fontWeight: 950, marginBottom: 8 }}>{label}</div>
                            {cleaned.map((it) => (
                              <div key={it.id} style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 12 }}>
                                <span style={{ fontWeight: 900, color: it.color || "rgba(31,42,36,.78)" }}>{it.name}</span>
                                <span style={{ fontWeight: 950, color: pctColor(it.value) }}>{fmtPct(it.value)}</span>
                              </div>
                            ))}
                          </div>
                        );
                      }}
                    />
                    {visibleSeries.map((r) => (
                      <Line key={r.id} type="monotone" dataKey={r.id} dot={false} strokeWidth={2} stroke={colorForSeries(r.id)} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="td-legend">
              {[...effectiveTop]
                .sort((a, b) => lastValue(b) - lastValue(a))
                .map((r) => (
                <div
                  key={r.id}
                  className={`td-chip ${hidden[r.id] ? "off" : ""}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => setHidden((h) => ({ ...h, [r.id]: !h[r.id] }))}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setHidden((h) => ({ ...h, [r.id]: !h[r.id] })); }}
                  title={t("clickToHideShow")}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 999,
                      background: hidden[r.id] ? "rgba(31,42,36,.20)" : colorForSeries(r.id),
                      flex: "0 0 auto",
                    }}
                  />
                  <span>{displayName(title, r.name, translateData)}</span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <div
              className="td-trendTableWrap"
              style={{ marginTop: 0, position: "relative" }}
              onMouseLeave={() => setTip(null)}
            >
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${(months || []).length}, minmax(56px, 1fr))`,
                    borderBottom: "1px solid rgba(30,40,34,.08)",
                    background: "rgba(181,154,91,.10)",
                  }}
                >
                  {(months || []).map((m) => (
                    <div key={m} style={{ padding: "6px 6px", fontSize: 9, fontWeight: 950, letterSpacing: ".03em", textTransform: "uppercase", textAlign: "center" }}>
                      {m}
                    </div>
                  ))}
                </div>
                {(() => {
                  const maxRows = Math.max(...columnRankings.map((c) => c.length), 0);
                  const ranks = Array.from({ length: maxRows }, (_, i) => i);
                  return ranks.map((rankIdx) => (
                    <div
                      key={rankIdx}
                      style={{
                        display: "grid",
                        gridTemplateColumns: `repeat(${(months || []).length}, minmax(56px, 1fr))`,
                        borderBottom: "1px solid rgba(30,40,34,.08)",
                        background: "#fff",
                      }}
                    >
                      {(months || []).map((m, colIdx) => {
                        const r = columnRankings[colIdx] && columnRankings[colIdx][rankIdx];
                        if (!r) {
                          return <div key={`${m}-empty-${rankIdx}`} style={{ padding: "6px 6px", background: "rgba(31,42,36,.03)" }} />;
                        }
                        const v = r.points && r.points[colIdx] ? r.points[colIdx].v : 0;
                        const bg = colorForValue(v, minV, maxV);
                        const label = displayName(title, r.name, translateData);
                        return (
                          <div
                            key={`${m}-${r.id}`}
                            style={{ padding: "6px 6px", background: bg, color: "rgba(255,255,255,.94)", cursor: "pointer", userSelect: "none" }}
                            onClick={() => setHidden((h) => ({ ...h, [r.id]: !h[r.id] }))}
                            onMouseEnter={(e) => {
                              const rect = e.currentTarget.getBoundingClientRect();
                              setTip({ rowName: r.name, month: m, value: v, x: rect.left + rect.width / 2, y: rect.top });
                            }}
                            onMouseLeave={() => setTip(null)}
                            title={`${r.name} · ${m} · ${fmtPct(v)}`}
                          >
                            <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", alignItems: "center", gap: 4, minHeight: 34 }}>
                              <span style={{ fontSize: 8, fontWeight: 950, opacity: 0.94, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%", width: "100%", textAlign: "center" }}>
                                {label}
                              </span>
                              <span style={{ fontSize: 10, fontWeight: 950, fontVariantNumeric: "tabular-nums" }}>{fmtPct(v)}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ));
                })()}
              </div>
              {tip ? <HeatTip tip={tip} t={t} /> : null}
            </div>
            <div className="td-legend">
              {effectiveTop.map((r) => (
                <div
                  key={r.id}
                  className={`td-chip ${hidden[r.id] ? "off" : ""}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => setHidden((h) => ({ ...h, [r.id]: !h[r.id] }))}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setHidden((h) => ({ ...h, [r.id]: !h[r.id] })); }}
                  title={t("clickToHideShow")}
                >
                  <span>{displayName(title, r.name, translateData)}</span>
                </div>
              ))}
            </div>
          </>
        )}
        <div style={{ marginTop: 10, fontSize: 12, fontWeight: 900, color: "rgba(31,42,36,.66)" }}>
          {t("heatmapHint")}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Chart View (export) ---------------- */
export default function MarketFlowChartView({ trend, months, sortKey, setTrendSort, chartMode, setChartMode, t, translateData }) {
  const monthLabels = useMemo(() => (months || []).map((m) => (typeof m === "string" ? m : m.label || m.key)), [months]);

  return (
    <div className="td-trendGrid">
      <TrendCard title={t("regions")} rows={trend?.regions} months={monthLabels} sortKey={sortKey} mode={chartMode} t={t} translateData={translateData} />
      <TrendCard title={t("countries")} rows={trend?.countries} months={monthLabels} sortKey={sortKey} mode={chartMode} t={t} translateData={translateData} />
      <TrendCard title={t("sectors")} rows={trend?.sectors} months={monthLabels} sortKey={sortKey} mode={chartMode} t={t} translateData={translateData} />
      <TrendCard title={t("marketCap")} rows={trend?.marketCaps} months={monthLabels} sortKey={sortKey} mode={chartMode} t={t} translateData={translateData} />
      <TrendCard title={t("styles")} rows={trend?.styles} months={monthLabels} sortKey={sortKey} mode={chartMode} t={t} translateData={translateData} />
      <TrendCard title={t("fundamentalScore")} rows={trend?.fundamentals} months={monthLabels} sortKey={sortKey} mode={chartMode} t={t} translateData={translateData} />
    </div>
  );
}
